import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  CancelTokenSource
} from 'axios';
import FormData from 'form-data';

export class AxiosCall {
  static async get(
    url: string,
    source?: CancelTokenSource,
    timeout: number = 0
  ): Promise<any> {
    try {
      if (source) {
        const response = await axios.get(url, {
          cancelToken: source.token,
          timeout: timeout
        });
        return response.data;
      } else {
        const response = await axios.get(url);
        return response.data;
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        return {};
      } else {
        // handle error
        if (err.response && err.response.data) {
          throw err.response.data;
        } else {
          throw err;
        }
      }
    }
  }

  static async getFileData(
    url: string,
    source?: CancelTokenSource,
    timeout: number = 0
  ): Promise<any> {
    try {
      if (source) {
        const response = await axios.get(url, {
          cancelToken: source.token,
          timeout: timeout,
          responseType: 'arraybuffer'
        });
        return response.data;
      } else {
        const response = await axios.get(url, {
          responseType: 'arraybuffer'
        });
        return response.data;
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        return {};
      } else {
        // handle error
        if (err.response && err.response.data) {
          throw err.response.data;
        } else {
          throw err;
        }
      }
    }
  }

  // Returning the entire response because, to download the file, we first need to convert it into a blob with its file type.
  // We can get the file type from the response's content type in its header.
  static async getFile({
    url,
    cancelToken,
    responseType = 'arraybuffer',
    timeout
  }: AxiosRequestConfig & {url: string}): Promise<AxiosResponse<any>> {
    return axios.get(url, {
      cancelToken,
      timeout,
      responseType
    });
  }

  static async post(
    url: string,
    payload: any,
    source?: CancelTokenSource,
    timeOut: number = 0
  ): Promise<any> {
    try {
      if (source) {
        const response = await axios.post(url, payload, {
          cancelToken: source.token,
          timeout: timeOut
        });
        return response.data;
      } else {
        const response = await axios.post(url, payload, {
          timeout: timeOut
        });
        return response.data;
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        throw err?.message;
      } else {
        if (err.response && err.response.data) {
          throw err.response.data;
        } else {
          throw err;
        }
      }
    }
  }

  static async postForm(
    url: string,
    payload: any,
    source?: CancelTokenSource,
    timeOut: number = 0
  ): Promise<any> {
    try {
      const form = new FormData();
      Object.keys(payload).forEach(key => {
        form.append(key, payload[key]);
      });
      const headers = {
        'Content-Type': 'multipart/form-data'
      };
      if (source) {
        const response = await axios.post(url, form, {
          headers,
          cancelToken: source.token,
          timeout: timeOut
        });
        return response.data;
      } else {
        const response = await axios.post(url, form, {
          headers,
          timeout: timeOut
        });
        return response.data;
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        throw err?.message;
      } else {
        if (err.response && err.response.data) {
          throw err.response.data;
        } else {
          throw err;
        }
      }
    }
  }

  static async put(
    url: string,
    payload: any,
    timeout: number = 0
  ): Promise<any> {
    try {
      const response = await axios.put(url, payload, {
        timeout: timeout
      });
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        throw err.response.data;
      } else {
        throw err;
      }
    }
  }

  static async patch(
    url: string,
    payload: any,
    timeout: number = 0
  ): Promise<any> {
    try {
      const response = await axios.patch(url, payload, {
        timeout: timeout
      });
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        throw err.response.data;
      } else {
        throw err;
      }
    }
  }

  static async delete(url: string, timeout: number = 0): Promise<any> {
    try {
      const response = await axios.delete(url, {
        timeout: timeout
      });
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        throw err.response.data;
      } else {
        throw err;
      }
    }
  }

  static async getImage(url: string, config: any): Promise<any> {
    try {
      const response = await axios.get(url, config);
      return response.data;
    } catch (err) {
      if (err && err.response && err.response.data) {
        throw err.response.data;
      } else {
        throw err;
      }
    }
  }
}
export {axios, CancelTokenSource};
